import { Image as ExpoImage } from 'expo-image';
import type { ImagePickerAsset } from 'expo-image-picker';
import * as ImageManipulator from 'expo-image-manipulator';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Modal,
  PanResponder,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  type ViewStyle,
  useWindowDimensions,
  View,
} from 'react-native';

import { Button } from '@/src/components/Button';
import {
  JPEG_CONTENT_TYPE,
  assetNeedsCompatibleImagePreview,
  blobFromMediaAsset,
} from '@/src/features/media/image-upload';
import { colors } from '@/src/theme/colors';
import { spacing } from '@/src/theme/spacing';

import {
  POST_IMAGE_CROP_ASPECT_PRESETS,
  cropPostImageAsset,
  postImageCropAspectRatio,
  postImageCropMaxOffset,
  postImageCropRectFromState,
  type PostImageCropRect,
  type PostImageCropOffset,
} from './post-image-crop';

const DEFAULT_CROP_ZOOM = 1.05;
const ORIGINAL_CROP_ZOOM = 1;
const MIN_CROP_ZOOM = 1;
const MAX_CROP_ZOOM = 4;
const ZOOM_STEP = 0.1;

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function getPointFromEvent(event: unknown): PostImageCropOffset | null {
  const nativeEvent =
    typeof event === 'object' && event && 'nativeEvent' in event
      ? (event as { nativeEvent?: unknown }).nativeEvent
      : event;
  const eventRecord =
    typeof nativeEvent === 'object' && nativeEvent
      ? (nativeEvent as Record<string, unknown>)
      : {};
  const touches = eventRecord.touches as
    | ArrayLike<{ clientX?: number; clientY?: number; pageX?: number; pageY?: number }>
    | undefined;
  const changedTouches = eventRecord.changedTouches as
    | ArrayLike<{ clientX?: number; clientY?: number; pageX?: number; pageY?: number }>
    | undefined;
  const touch = touches?.[0] ?? changedTouches?.[0];
  const x =
    typeof eventRecord.clientX === 'number'
      ? eventRecord.clientX
      : typeof touch?.clientX === 'number'
        ? touch.clientX
        : typeof eventRecord.pageX === 'number'
          ? eventRecord.pageX
          : touch?.pageX;
  const y =
    typeof eventRecord.clientY === 'number'
      ? eventRecord.clientY
      : typeof touch?.clientY === 'number'
        ? touch.clientY
        : typeof eventRecord.pageY === 'number'
          ? eventRecord.pageY
          : touch?.pageY;

  if (typeof x !== 'number' || typeof y !== 'number') {
    return null;
  }

  return { x, y };
}

function stopWebEvent(event: unknown) {
  const maybeEvent = event as {
    preventDefault?: () => void;
    stopPropagation?: () => void;
  };

  maybeEvent.preventDefault?.();
  maybeEvent.stopPropagation?.();
}

async function loadWebImageElement(sourceUri: string) {
  const image = await new Promise<HTMLImageElement>((resolve, reject) => {
    const element = document.createElement('img');

    element.onload = () => resolve(element);
    element.onerror = () => reject(new Error('Unable to load crop preview.'));
    element.decoding = 'async';
    element.src = sourceUri;
  });
  await image.decode?.().catch(() => undefined);

  return image;
}

async function loadWebImageDimensions(sourceUri: string) {
  if (Platform.OS !== 'web' || typeof document === 'undefined') {
    return null;
  }

  const image = await loadWebImageElement(sourceUri);

  if (image.naturalWidth <= 0 || image.naturalHeight <= 0) {
    return null;
  }

  return {
    height: image.naturalHeight,
    width: image.naturalWidth,
  };
}

function frameSizeForAsset({
  asset,
  aspect,
  originalMode,
  viewportHeight,
  viewportWidth,
}: {
  asset: ImagePickerAsset | null;
  aspect: number;
  originalMode: boolean;
  viewportHeight: number;
  viewportWidth: number;
}) {
  let width = Math.min(Math.max(240, viewportWidth - 72), 560);
  let height = width / aspect;
  const maxHeight = Math.max(220, viewportHeight * 0.42);

  if (height > maxHeight) {
    height = maxHeight;
    width = height * aspect;
  }

  if (originalMode) {
    const sourceWidth = Math.max(1, asset?.width || width);
    const sourceHeight = Math.max(1, asset?.height || height);
    const containScale = Math.min(width / sourceWidth, height / sourceHeight);

    width = sourceWidth * containScale;
    height = sourceHeight * containScale;
  }

  return {
    height,
    width,
  };
}

function useCropPreviewURI(asset: ImagePickerAsset | null) {
  const [preview, setPreview] = useState<{
    failed: boolean;
    height: number | null;
    loading: boolean;
    uri: string | null;
    width: number | null;
  }>({
    failed: false,
    height: null,
    loading: false,
    uri: null,
    width: null,
  });

  useEffect(() => {
    if (!asset) {
      setPreview({
        failed: false,
        height: null,
        loading: false,
        uri: null,
        width: null,
      });
      return;
    }

    const selectedAsset = asset;
    const needsCompatiblePreview = assetNeedsCompatibleImagePreview(asset);
    let active = true;
    let objectURL: string | null = null;

    if (!needsCompatiblePreview) {
      setPreview({
        failed: false,
        height: selectedAsset.height ?? null,
        loading: false,
        uri: asset.uri,
        width: selectedAsset.width ?? null,
      });

      if (Platform.OS === 'web') {
        void loadWebImageDimensions(asset.uri)
          .then((dimensions) => {
            if (!active || !dimensions) {
              return;
            }

            setPreview({
              failed: false,
              height: dimensions.height,
              loading: false,
              uri: asset.uri,
              width: dimensions.width,
            });
          })
          .catch(() => undefined);
      }

      return () => {
        active = false;
      };
    }

    setPreview({
      failed: false,
      height: null,
      loading: true,
      uri: null,
      width: null,
    });

    async function preparePreview() {
      if (
        Platform.OS === 'web' &&
        typeof URL !== 'undefined' &&
        typeof URL.createObjectURL === 'function'
      ) {
        try {
          const sourceBlob = await blobFromMediaAsset(
            selectedAsset,
            'That HEIC photo could not be read for cropping.',
          );
          const { default: heic2any } = await import('heic2any');
          const converted = await heic2any({
            blob: sourceBlob,
            quality: 0.9,
            toType: JPEG_CONTENT_TYPE,
          });
          const convertedBlob = Array.isArray(converted)
            ? converted[0]
            : converted;

          if (!convertedBlob || convertedBlob.size <= 0) {
            throw new Error('HEIC crop preview conversion produced an empty image.');
          }

          objectURL = URL.createObjectURL(convertedBlob);
          const dimensions = await loadWebImageDimensions(objectURL);

          if (!active) {
            URL.revokeObjectURL(objectURL);
            return;
          }

          setPreview({
            failed: false,
            height: dimensions?.height ?? selectedAsset.height ?? null,
            loading: false,
            uri: objectURL,
            width: dimensions?.width ?? selectedAsset.width ?? null,
          });
          return;
        } catch {
          // Try Expo's manipulator below. Native builds can often read HEIC.
        }
      }

      try {
        const result = await ImageManipulator.manipulateAsync(
          selectedAsset.uri,
          [],
          {
            compress: 0.9,
            format: ImageManipulator.SaveFormat.JPEG,
          },
        );

        if (!active) {
          return;
        }

        setPreview({
          failed: false,
          height: result.height,
          loading: false,
          uri: result.uri,
          width: result.width,
        });
      } catch {
        if (!active) {
          return;
        }

        setPreview({
          failed: true,
          height: null,
          loading: false,
          uri: null,
          width: null,
        });
      }
    }

    void preparePreview();

    return () => {
      active = false;
      if (objectURL) {
        URL.revokeObjectURL(objectURL);
      }
    };
  }, [asset]);

  return preview;
}

async function cropPostImageOnWeb({
  asset,
  crop,
  sourceUri,
}: {
  asset: ImagePickerAsset;
  crop: PostImageCropRect;
  sourceUri: string;
}) {
  if (
    Platform.OS !== 'web' ||
    typeof document === 'undefined' ||
    typeof URL === 'undefined' ||
    typeof URL.createObjectURL !== 'function'
  ) {
    return null;
  }

  const image = await loadWebImageElement(sourceUri);
  const canvas = document.createElement('canvas');
  const width = Math.max(1, Math.round(crop.width));
  const height = Math.max(1, Math.round(crop.height));
  const scaleX = image.naturalWidth / Math.max(1, asset.width || image.naturalWidth);
  const scaleY =
    image.naturalHeight / Math.max(1, asset.height || image.naturalHeight);
  const context = canvas.getContext('2d');

  if (!context) {
    throw new Error('Canvas is unavailable for photo cropping.');
  }

  canvas.width = width;
  canvas.height = height;
  context.drawImage(
    image,
    Math.max(0, Math.round(crop.originX * scaleX)),
    Math.max(0, Math.round(crop.originY * scaleY)),
    Math.max(1, Math.round(crop.width * scaleX)),
    Math.max(1, Math.round(crop.height * scaleY)),
    0,
    0,
    width,
    height,
  );

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (result) => {
        if (result) {
          resolve(result);
          return;
        }

        reject(new Error('Photo crop produced an empty image.'));
      },
      JPEG_CONTENT_TYPE,
      0.9,
    );
  });
  const uri = canvas.toDataURL(JPEG_CONTENT_TYPE, 0.9);
  const fileName = `${(asset.fileName ?? 'photo')
    .split('?')[0]
    ?.split('#')[0]
    ?.split('/')
    .filter(Boolean)
    .pop()
    ?.replace(/\.[^.]+$/u, '')
    .replace(/[^a-zA-Z0-9._-]/g, '_') || 'photo'}-cropped.jpg`;

  return {
    ...asset,
    file:
      typeof File !== 'undefined'
        ? new File([blob], fileName, { type: JPEG_CONTENT_TYPE })
        : undefined,
    fileName,
    fileSize: blob.size,
    height,
    mimeType: JPEG_CONTENT_TYPE,
    uri,
    width,
  };
}

export function PostImageCropperModal({
  asset,
  onCancel,
  onComplete,
  visible,
}: {
  asset: ImagePickerAsset | null;
  onCancel: () => void;
  onComplete: (asset: ImagePickerAsset) => void;
  visible: boolean;
}) {
  const { height: viewportHeight, width: viewportWidth } = useWindowDimensions();
  const [cropError, setCropError] = useState<string | null>(null);
  const [cropAspectKey, setCropAspectKey] =
    useState<(typeof POST_IMAGE_CROP_ASPECT_PRESETS)[number]['key']>(
      'original',
    );
  const [cropping, setCropping] = useState(false);
  const [offset, setOffset] = useState<PostImageCropOffset>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(DEFAULT_CROP_ZOOM);
  const maxOffsetRef = useRef<PostImageCropOffset>({ x: 0, y: 0 });
  const offsetRef = useRef<PostImageCropOffset>({ x: 0, y: 0 });
  const panStart = useRef<PostImageCropOffset>({ x: 0, y: 0 });
  const webDrag = useRef<{
    cleanup?: () => void;
    startOffset: PostImageCropOffset;
    startPoint: PostImageCropOffset;
  } | null>(null);
  const preview = useCropPreviewURI(asset);
  const cropAspectPreset =
    POST_IMAGE_CROP_ASPECT_PRESETS.find(
      (preset) => preset.key === cropAspectKey,
    ) ?? POST_IMAGE_CROP_ASPECT_PRESETS[0];
  const measuredAsset = useMemo(
    () =>
      asset
        ? {
            ...asset,
            height: preview.height ?? asset.height,
            width: preview.width ?? asset.width,
          }
        : null,
    [asset, preview.height, preview.width],
  );
  const cropAspect = postImageCropAspectRatio({
    asset: measuredAsset,
    preset: cropAspectPreset,
  });
  const originalMode = cropAspectPreset.key === 'original';
  const frame = useMemo(
    () =>
      frameSizeForAsset({
        aspect: cropAspect,
        asset: measuredAsset,
        originalMode,
        viewportHeight,
        viewportWidth,
      }),
    [measuredAsset, cropAspect, originalMode, viewportHeight, viewportWidth],
  );
  const sourceWidth = Math.max(1, measuredAsset?.width || frame.width);
  const sourceHeight = Math.max(1, measuredAsset?.height || frame.height);
  const imageScale =
    (originalMode
      ? Math.min(frame.width / sourceWidth, frame.height / sourceHeight)
      : Math.max(frame.width / sourceWidth, frame.height / sourceHeight)) *
    zoom;
  const imageWidth = sourceWidth * imageScale;
  const imageHeight = sourceHeight * imageScale;
  const maxOffset = useMemo(
    () =>
      postImageCropMaxOffset({
        asset: {
          height: sourceHeight,
          width: sourceWidth,
        },
        fit: originalMode ? 'contain' : 'cover',
        frameHeight: frame.height,
        frameWidth: frame.width,
        zoom,
      }),
    [frame.height, frame.width, originalMode, sourceHeight, sourceWidth, zoom],
  );
  maxOffsetRef.current = maxOffset;

  const commitOffset = useCallback((nextOffset: PostImageCropOffset) => {
    const bounds = maxOffsetRef.current;
    const clampedOffset = {
      x: clamp(nextOffset.x, -bounds.x, bounds.x),
      y: clamp(nextOffset.y, -bounds.y, bounds.y),
    };

    offsetRef.current = clampedOffset;
    setOffset(clampedOffset);
  }, []);

  useEffect(() => {
    const nextOffset = { x: 0, y: 0 };

    offsetRef.current = nextOffset;
    setOffset(nextOffset);
    setCropAspectKey('original');
    setCropError(null);
    setZoom(ORIGINAL_CROP_ZOOM);
  }, [asset?.uri]);

  function selectCropAspect(
    nextAspectKey: (typeof POST_IMAGE_CROP_ASPECT_PRESETS)[number]['key'],
  ) {
    const nextOffset = { x: 0, y: 0 };

    setCropAspectKey(nextAspectKey);
    offsetRef.current = nextOffset;
    setOffset(nextOffset);
    setZoom(nextAspectKey === 'original' ? ORIGINAL_CROP_ZOOM : DEFAULT_CROP_ZOOM);
  }

  useEffect(() => {
    maxOffsetRef.current = maxOffset;
    setOffset((current) => {
      const clampedOffset = {
        x: clamp(current.x, -maxOffset.x, maxOffset.x),
        y: clamp(current.y, -maxOffset.y, maxOffset.y),
      };

      offsetRef.current = clampedOffset;

      return clampedOffset;
    });
  }, [maxOffset]);

  useEffect(
    () => () => {
      webDrag.current?.cleanup?.();
    },
    [],
  );

  const handleWebPointerDown = useCallback(
    (event: unknown) => {
      if (Platform.OS !== 'web') {
        return;
      }

      const point = getPointFromEvent(event);

      if (!point) {
        return;
      }

      stopWebEvent(event);
      webDrag.current?.cleanup?.();

      const handleMove = (moveEvent: unknown) => {
        const movePoint = getPointFromEvent(moveEvent);
        const activeDrag = webDrag.current;

        if (!movePoint || !activeDrag) {
          return;
        }

        stopWebEvent(moveEvent);
        commitOffset({
          x: activeDrag.startOffset.x + movePoint.x - activeDrag.startPoint.x,
          y: activeDrag.startOffset.y + movePoint.y - activeDrag.startPoint.y,
        });
      };

      const handleEnd = (endEvent: unknown) => {
        stopWebEvent(endEvent);
        webDrag.current?.cleanup?.();
        webDrag.current = null;
      };

      const cleanup = () => {
        window.document.removeEventListener('pointermove', handleMove);
        window.document.removeEventListener('pointerup', handleEnd);
        window.document.removeEventListener('pointercancel', handleEnd);
      };

      window.document.addEventListener('pointermove', handleMove, {
        passive: false,
      });
      window.document.addEventListener('pointerup', handleEnd, {
        passive: false,
      });
      window.document.addEventListener('pointercancel', handleEnd, {
        passive: false,
      });

      webDrag.current = {
        cleanup,
        startOffset: offsetRef.current,
        startPoint: point,
      };
    },
    [commitOffset],
  );

  const panResponder = useMemo(
    () =>
      PanResponder.create({
        onStartShouldSetPanResponder: () => true,
        onStartShouldSetPanResponderCapture: () => true,
        onMoveShouldSetPanResponder: (_, gesture) =>
          Math.abs(gesture.dx) > 2 || Math.abs(gesture.dy) > 2,
        onMoveShouldSetPanResponderCapture: (_, gesture) =>
          Math.abs(gesture.dx) > 2 || Math.abs(gesture.dy) > 2,
        onPanResponderGrant: () => {
          panStart.current = offsetRef.current;
        },
        onPanResponderMove: (_, gesture) => {
          commitOffset({
            x: clamp(
              panStart.current.x + gesture.dx,
              -maxOffset.x,
              maxOffset.x,
            ),
            y: clamp(
              panStart.current.y + gesture.dy,
              -maxOffset.y,
              maxOffset.y,
            ),
          });
        },
        onPanResponderTerminationRequest: () => false,
        onShouldBlockNativeResponder: () => true,
      }),
    [commitOffset, maxOffset.x, maxOffset.y],
  );
  const cropFrameHandlers =
    Platform.OS === 'web'
      ? ({ onPointerDown: handleWebPointerDown } as Record<string, unknown>)
      : panResponder.panHandlers;

  async function handleUseCrop() {
    if (!asset || !measuredAsset || !preview.uri) {
      return;
    }

    setCropping(true);
    setCropError(null);

    try {
      const crop = postImageCropRectFromState({
        asset: measuredAsset,
        fit: originalMode ? 'contain' : 'cover',
        frameHeight: frame.height,
        frameWidth: frame.width,
        offset,
        zoom,
      });
      const croppedAsset =
        (await cropPostImageOnWeb({
          asset: measuredAsset,
          crop,
          sourceUri: preview.uri,
        })) ??
        (await cropPostImageAsset({
          asset: measuredAsset,
          crop,
          sourceUri: preview.uri,
        }));

      onComplete(croppedAsset);
    } catch {
      setCropError('This photo could not be cropped. Please try another photo.');
    } finally {
      setCropping(false);
    }
  }

  return (
    <Modal
      animationType="fade"
      transparent
      visible={visible}
      onRequestClose={onCancel}
    >
      <Pressable
        disabled={cropping}
        style={styles.backdrop}
        onPress={onCancel}
      >
        <Pressable
          style={styles.panel}
          onPress={(event) => event.stopPropagation()}
        >
          <View style={styles.header}>
            <View style={styles.titleBlock}>
              <Text style={styles.title}>Crop photo</Text>
              <Text style={styles.subtitle}>
                Drag the photo and adjust zoom before saving.
              </Text>
            </View>
            <Pressable
              accessibilityLabel="Close photo cropper"
              accessibilityRole="button"
              disabled={cropping}
              onPress={onCancel}
              style={styles.closeButton}
            >
              <Text style={styles.closeText}>x</Text>
            </Pressable>
          </View>

          <ScrollView
            contentContainerStyle={styles.content}
            showsVerticalScrollIndicator={false}
            style={styles.scroll}
          >
            <View
              {...cropFrameHandlers}
              style={[
                styles.cropFrame,
                Platform.OS === 'web' ? styles.webCropFrame : null,
                {
                  height: frame.height,
                  width: frame.width,
                },
              ]}
            >
              {preview.uri ? (
                <ExpoImage
                  contentFit={originalMode ? 'contain' : 'cover'}
                  pointerEvents="none"
                  source={{ uri: preview.uri }}
                  style={[
                    styles.cropImage,
                    originalMode
                      ? {
                          height: frame.height * zoom,
                          left: (frame.width - frame.width * zoom) / 2 + offset.x,
                          top:
                            (frame.height - frame.height * zoom) / 2 +
                            offset.y,
                          width: frame.width * zoom,
                        }
                      : {
                          height: imageHeight,
                          left: (frame.width - imageWidth) / 2 + offset.x,
                          top: (frame.height - imageHeight) / 2 + offset.y,
                          width: imageWidth,
                        },
                  ]}
                />
              ) : (
                <View style={styles.loadingCropFrame}>
                  {preview.loading ? (
                    <ActivityIndicator color={colors.primary} size="small" />
                  ) : null}
                  <Text style={styles.loadingCropText}>
                    {preview.loading
                      ? 'Preparing photo...'
                      : 'This photo cannot be previewed for cropping.'}
                  </Text>
                </View>
              )}
              <View pointerEvents="none" style={styles.cropBorder} />
            </View>

            {cropError || preview.failed ? (
              <View style={styles.fallbackNotice}>
                <Text style={styles.fallbackText}>
                  {cropError ??
                    'This photo format could not be prepared for cropping.'}
                </Text>
              </View>
            ) : null}
          </ScrollView>

          <View style={styles.footer}>
            <View style={styles.aspectRow}>
              {POST_IMAGE_CROP_ASPECT_PRESETS.map((preset) => {
                const selected = cropAspectKey === preset.key;

                return (
                  <Pressable
                    key={preset.key}
                    accessibilityRole="button"
                    accessibilityState={{ selected }}
                    disabled={cropping}
                    onPress={() => selectCropAspect(preset.key)}
                    style={[
                      styles.aspectButton,
                      selected ? styles.aspectButtonSelected : null,
                    ]}
                  >
                    <Text
                      style={[
                        styles.aspectButtonText,
                        selected ? styles.aspectButtonTextSelected : null,
                      ]}
                    >
                      {preset.label}
                    </Text>
                  </Pressable>
                );
              })}
            </View>

            <View style={styles.zoomRow}>
              <Button
                title="-"
                variant="secondary"
                disabled={cropping || !preview.uri}
                onPress={() =>
                  setZoom((value) => Math.max(MIN_CROP_ZOOM, value - ZOOM_STEP))
                }
                style={styles.zoomButton}
              />
              <Text style={styles.zoomText}>{Math.round(zoom * 100)}%</Text>
              <Button
                title="+"
                variant="secondary"
                disabled={cropping || !preview.uri}
                onPress={() =>
                  setZoom((value) => Math.min(MAX_CROP_ZOOM, value + ZOOM_STEP))
                }
                style={styles.zoomButton}
              />
            </View>

            <View style={styles.actions}>
              <Button
                title="Cancel"
                variant="secondary"
                disabled={cropping}
                onPress={onCancel}
                style={styles.actionButton}
              />
              <Button
                title="Use crop"
                disabled={!preview.uri}
                loading={cropping}
                onPress={handleUseCrop}
                style={styles.actionButton}
              />
            </View>
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const styles = StyleSheet.create({
  actionButton: {
    flex: 1,
  },
  actions: {
    flexDirection: 'row',
    gap: spacing.md,
    width: '100%',
  },
  aspectButton: {
    alignItems: 'center',
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 999,
    borderWidth: 1,
    minHeight: 36,
    justifyContent: 'center',
    paddingHorizontal: spacing.md,
  },
  aspectButtonSelected: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  aspectButtonText: {
    color: colors.secondary,
    fontSize: 13,
    fontWeight: '900',
  },
  aspectButtonTextSelected: {
    color: colors.surface,
  },
  aspectRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
    justifyContent: 'center',
    width: '100%',
  },
  backdrop: {
    alignItems: 'center',
    backgroundColor: 'rgba(26, 12, 62, 0.48)',
    flex: 1,
    justifyContent: 'center',
    padding: spacing.lg,
  },
  closeButton: {
    alignItems: 'center',
    height: 36,
    justifyContent: 'center',
    width: 36,
  },
  closeText: {
    color: colors.text,
    fontSize: 24,
    fontWeight: '700',
  },
  cropBorder: {
    borderColor: colors.primary,
    borderWidth: 3,
    bottom: 0,
    left: 0,
    position: 'absolute',
    right: 0,
    top: 0,
  },
  cropFrame: {
    backgroundColor: colors.text,
    alignSelf: 'center',
    maxWidth: '100%',
    overflow: 'hidden',
    position: 'relative',
  },
  cropImage: {
    position: 'absolute',
  },
  content: {
    alignItems: 'center',
    gap: spacing.md,
    padding: spacing.lg,
  },
  fallbackNotice: {
    alignItems: 'flex-start',
    backgroundColor: colors.surfaceTint,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    padding: spacing.md,
    width: '100%',
  },
  fallbackText: {
    color: colors.text,
    fontSize: 14,
    lineHeight: 20,
  },
  header: {
    alignItems: 'flex-start',
    borderBottomColor: colors.border,
    borderBottomWidth: StyleSheet.hairlineWidth,
    flexDirection: 'row',
    gap: spacing.md,
    justifyContent: 'space-between',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    width: '100%',
  },
  loadingCropFrame: {
    alignItems: 'center',
    gap: spacing.sm,
    height: '100%',
    justifyContent: 'center',
    padding: spacing.md,
    width: '100%',
  },
  loadingCropText: {
    color: colors.surface,
    fontSize: 14,
    fontWeight: '800',
    lineHeight: 18,
    textAlign: 'center',
  },
  panel: {
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderColor: colors.border,
    borderRadius: 8,
    borderWidth: 1,
    maxHeight: '92%',
    maxWidth: 780,
    overflow: 'hidden',
    width: '100%',
  },
  footer: {
    backgroundColor: colors.surface,
    borderTopColor: colors.border,
    borderTopWidth: StyleSheet.hairlineWidth,
    gap: spacing.sm,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    width: '100%',
  },
  scroll: {
    flexGrow: 0,
    flexShrink: 1,
    width: '100%',
  },
  subtitle: {
    color: colors.mutedText,
    fontSize: 14,
    lineHeight: 20,
    marginTop: spacing.xs,
  },
  title: {
    color: colors.text,
    fontSize: 20,
    fontWeight: '900',
  },
  titleBlock: {
    flex: 1,
    minWidth: 0,
  },
  webCropFrame: {
    cursor: 'grab',
    touchAction: 'none',
    userSelect: 'none',
  } as unknown as ViewStyle,
  zoomButton: {
    minWidth: 64,
  },
  zoomRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.md,
    justifyContent: 'center',
  },
  zoomText: {
    color: colors.text,
    fontSize: 15,
    fontWeight: '900',
    minWidth: 52,
    textAlign: 'center',
  },
});
